// Thin wrapper that pulls configs + collections from the API, caches
// in memory, and dispatches change events. Components use the hooks
// below; they get current data + re-render when something is saved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { z, type ZodType } from 'zod';
import { apiGet } from './api';
import { subscribeCmsChange } from './cmsEvents';
import {
  articleRowSchema, serviceRowSchema, offerRowSchema, projectRowSchema,
  testimonialRowSchema, experienceRowSchema, certificationRowSchema,
  speakingEventRowSchema, thoughtLeadershipRowSchema, socialLinkRowSchema,
  mediaRowSchema, servicePageRowSchema,
  configBlobSchemas,
} from '@/schemas';
import {
  cmsCollectionCreate,
  cmsCollectionUpdate,
  cmsCollectionRemove,
  cmsCollectionBulkRemove,
  cmsCollectionReorder,
  type CollectionScope,
} from './cmsStore.collections';

// Scope → row schema map. Used by useCollection() to wire Zod parsing into
// the single GET path that every section/page reads through. Drift caught
// by the drift test in app/src/test/schemas/.
const collectionRowSchemas: Partial<Record<string, ZodType<unknown>>> = {
  articles: articleRowSchema,
  services: serviceRowSchema,
  offers: offerRowSchema,
  projects: projectRowSchema,
  testimonials: testimonialRowSchema,
  experience: experienceRowSchema,
  certifications: certificationRowSchema,
  'speaking-events': speakingEventRowSchema,
  'thought-leadership': thoughtLeadershipRowSchema,
  'social-links': socialLinkRowSchema,
  media: mediaRowSchema,
  'service-pages': servicePageRowSchema,
  // conversations: skipped — admin paginated list is a wrapper object,
  // not a row array. conversationsApi.ts wires its own schema.
};

type Fetcher<T> = () => Promise<T>;

interface ResourceState<T> {
  value: T;
  isLoading: boolean;
  isError: boolean;
}

function readLocalCache<T>(key: string, fallback: T): T {
  try {
    const cached = localStorage.getItem(`ck_cache_${key}`);
    if (cached) return JSON.parse(cached) as T;
  } catch { /* ignore */ }
  return fallback;
}

function useResource<T>(key: string, fetcher: Fetcher<T>, initial: T, relevantScopes: string[]): ResourceState<T> {
  const [state, setState] = useState<ResourceState<T>>(() => {
    const cached = readLocalCache<T | undefined>(key, undefined);
    return {
      value: cached ?? initial,
      // Treat "no cache hit on first mount" as loading so admin managers can
      // show a Skeleton until the network fetch resolves. Once a value exists
      // in localStorage we have something to render and isLoading is false.
      isLoading: cached === undefined,
      isError: false,
    };
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Bridge optimistic writes: on any emit, re-read the localStorage cache
      // synchronously before firing the network call. cmsCollection* helpers
      // write to the same cache key, so subscribers see the optimistic state
      // immediately. The async fetch then overwrites with server truth.
      const cached = readLocalCache<T | undefined>(key, undefined);
      if (!cancelled && cached !== undefined) {
        setState((s) => ({ ...s, value: cached }));
      }
      try {
        const data = await fetcher();
        if (cancelled) return;
        setState({ value: data, isLoading: false, isError: false });
        try { localStorage.setItem(`ck_cache_${key}`, JSON.stringify(data)); } catch { /* ignore */ }
      } catch (err) {
        console.warn(`[siteData] ${key} fetch failed, using fallback`, err);
        if (cancelled) return;
        setState((s) => ({ ...s, isLoading: false, isError: true }));
      }
    };
    load();
    const unsub = subscribeCmsChange((scope) => {
      if (scope === 'all' || relevantScopes.includes(scope)) load();
    });
    return () => { cancelled = true; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

// ── Config map (single bulk request) ──────────────────────────────────────
//
// All useConfig() consumers share one /api/config call instead of one per key.
// The bulk endpoint already returns every readable key as a map; we fetch it
// once per session (or after a config invalidation), cache the Promise, and
// have useConfig select its own key from the resolved map.

type ConfigMap = Record<string, unknown>;

let configMapPromise: Promise<ConfigMap> | null = null;

/**
 * Validate each known config-blob key in the raw map against its Zod schema
 * (Phase 2.4). Per-key failures are logged and the key is dropped from the
 * returned map — useConfig's caller-supplied fallback takes over for that key.
 * Unknown keys (no schema mapped) pass through unchanged.
 */
function parseConfigMap(raw: unknown): ConfigMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const input = raw as Record<string, unknown>;
  const out: ConfigMap = {};
  for (const [key, value] of Object.entries(input)) {
    const schema = configBlobSchemas[key];
    if (!schema) {
      // Unknown key — forward through. Admin may have stored arbitrary blobs
      // that don't yet have a schema; we don't want to silently drop them.
      out[key] = value;
      continue;
    }
    const parsed = schema.safeParse(value);
    if (parsed.success) {
      out[key] = parsed.data;
    } else {
      console.warn(`[siteData] config blob ${key} failed schema parse — dropping`, parsed.error.issues);
    }
  }
  return out;
}

function fetchConfigMap(): Promise<ConfigMap> {
  if (!configMapPromise) {
    configMapPromise = apiGet<ConfigMap>('/api/config').then(
      (data) => parseConfigMap(data),
      (err) => {
        // Don't poison the cache on failure — let the next call retry.
        configMapPromise = null;
        throw err;
      },
    );
  }
  return configMapPromise;
}

/**
 * Invalidate the cached config map. Called by cmsEvents subscribers when an
 * admin edit fires a 'config' or 'all' scope change so the next useConfig read
 * refetches the bulk endpoint.
 */
export function invalidateConfigMap(): void {
  configMapPromise = null;
}

// Module-level: any successful POST/PUT/DELETE through api.ts dispatches a
// 'ck:api-cache-bust' window event. If the busted prefix touches /api/config,
// drop the cached bulk map so the next read pulls fresh data.
if (typeof window !== 'undefined') {
  window.addEventListener('ck:api-cache-bust', (ev: Event) => {
    const detail = (ev as CustomEvent<{ prefix?: string }>).detail;
    const prefix = detail?.prefix ?? '';
    if (prefix === '/api/config' || prefix.startsWith('/api/config')) {
      invalidateConfigMap();
    }
  });
}

function readSeededConfig<T>(key: string, fallback: T): T {
  try {
    const cached = localStorage.getItem(`ck_cache_config_${key}`);
    if (cached) return JSON.parse(cached) as T;
  } catch { /* ignore */ }
  return fallback;
}

// ── Config (single-row JSON blobs) ─────────────────────────
export function useConfig<T>(key: string, fallback: T): T {
  const [value, setValue] = useState<T>(() => readSeededConfig<T>(key, fallback));

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchConfigMap()
        .then((map) => {
          if (cancelled) return;
          const next = (key in map ? (map[key] as T) : fallback);
          setValue(next);
          try { localStorage.setItem(`ck_cache_config_${key}`, JSON.stringify(next)); } catch { /* ignore */ }
        })
        .catch((err) => {
          console.warn(`[siteData] config map fetch failed, using cached/fallback for ${key}`, err);
        });
    };
    load();
    const unsub = subscribeCmsChange((scope) => {
      if (scope === 'all' || scope === 'config') {
        invalidateConfigMap();
        load();
      }
    });
    return () => { cancelled = true; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return value;
}

// Content sections (useContentSection / useContentSections) were retired in
// the admin-consolidation v1 pass. Every public section now reads its
// heading/body/visible from its *_config blob via useConfig. The
// content_sections table + /api/content GET endpoints linger for one release
// with a Deprecation header.

// ── Collections ────────────────────────────────────────────
//
// The single read+write API every admin manager (and every public section)
// uses. Reads land in `data`; writes go through the cmsCollection* helpers,
// which mirror the optimistic state into the same `ck_cache_coll_<scope>`
// localStorage key the read path serves out of.
//
// Mutation methods are exposed for every scope but are only meaningful for
// scopes that map to the CRUD factory (CollectionScope). For 'conversations'
// — which has bespoke routes and richer mutations — admin code should use
// useConversations() (added in T3.5). Calling create/update/remove on the
// conversations scope from useCollection rejects with a clear error.

export type UseCollectionScope =
  | 'articles' | 'services' | 'offers' | 'projects' | 'testimonials'
  | 'experience' | 'certifications' | 'speaking-events'
  | 'thought-leadership' | 'social-links' | 'media' | 'service-pages'
  | 'conversations';

export interface UseCollectionResult<T> {
  data: T[];
  isLoading: boolean;
  isError: boolean;
  create: (row: Partial<T>) => Promise<T>;
  update: (id: string, patch: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
  bulkRemove: (ids: string[]) => Promise<{ deleted: string[]; notFound: string[] }>;
  reorder: (orderedIds: string[]) => Promise<void>;
}

const CONVERSATIONS_MUTATION_GUARD =
  'useCollection mutations are not supported for the "conversations" scope. ' +
  'Use useConversations() (cmsStore.conversations) instead.';

export function useCollection<T>(scope: UseCollectionScope): UseCollectionResult<T> {
  const rowSchema = collectionRowSchemas[scope];
  // When a schema is mapped, parse `/api/<scope>` responses as z.array(row).
  // Parse failures bubble through api.ts as ApiSchemaError → ck:sync-fail.
  const schema = rowSchema ? (z.array(rowSchema) as ZodType<T[]>) : undefined;
  const state = useResource<T[]>(
    `coll_${scope}`,
    () => apiGet<T[]>(`/api/${scope}`, { schema }),
    [],
    [scope, 'all'],
  );

  // Mutations close over `scope` — every manager gets the same call signature
  // and the underlying mechanics (optimistic cache write → emitCmsChange →
  // syncToServer) are guaranteed to be identical across collections.
  const isCrudScope = scope !== 'conversations';
  const collectionScope = scope as CollectionScope;

  const create = useCallback<UseCollectionResult<T>['create']>(
    (row) => {
      if (!isCrudScope) return Promise.reject(new Error(CONVERSATIONS_MUTATION_GUARD));
      return cmsCollectionCreate<Record<string, unknown>>(
        collectionScope,
        row as Record<string, unknown>,
      ) as Promise<T>;
    },
    [collectionScope, isCrudScope],
  );

  const update = useCallback<UseCollectionResult<T>['update']>(
    (id, patch) => {
      if (!isCrudScope) return Promise.reject(new Error(CONVERSATIONS_MUTATION_GUARD));
      return cmsCollectionUpdate<Record<string, unknown>>(
        collectionScope,
        id,
        patch as Record<string, unknown>,
      ) as Promise<T>;
    },
    [collectionScope, isCrudScope],
  );

  const remove = useCallback<UseCollectionResult<T>['remove']>(
    (id) => {
      if (!isCrudScope) return Promise.reject(new Error(CONVERSATIONS_MUTATION_GUARD));
      return cmsCollectionRemove(collectionScope, id);
    },
    [collectionScope, isCrudScope],
  );

  const bulkRemove = useCallback<UseCollectionResult<T>['bulkRemove']>(
    (ids) => {
      if (!isCrudScope) return Promise.reject(new Error(CONVERSATIONS_MUTATION_GUARD));
      return cmsCollectionBulkRemove(collectionScope, ids);
    },
    [collectionScope, isCrudScope],
  );

  const reorder = useCallback<UseCollectionResult<T>['reorder']>(
    (orderedIds) => {
      if (!isCrudScope) return Promise.reject(new Error(CONVERSATIONS_MUTATION_GUARD));
      return cmsCollectionReorder(collectionScope, orderedIds);
    },
    [collectionScope, isCrudScope],
  );

  return useMemo(
    () => ({
      data: state.value,
      isLoading: state.isLoading,
      isError: state.isError,
      create,
      update,
      remove,
      bulkRemove,
      reorder,
    }),
    [state.value, state.isLoading, state.isError, create, update, remove, bulkRemove, reorder],
  );
}
